import { Check, X } from "lucide-react";
import { num, istTime, STRATEGY_LABELS, isStructure } from "@/lib/api";

const RULES = [
  { key: "breakout", label: "Opening-range breakout", hint: (s) => `H ${num(s.orh)} / L ${num(s.orl)}` },
  { key: "vwap", label: "Price vs VWAP", hint: (s) => `VWAP ${num(s.vwap)}` },
  { key: "ema", label: "EMA fast vs slow", hint: (s) => `${num(s.ema_f)} / ${num(s.ema_s)}` },
  { key: "rsi", label: "RSI filter", hint: (s) => `RSI ${num(s.rsi)}` },
  { key: "regime", label: "Regime filter", hint: (s) => `ADX ${num(s.adx)} · OR ${num(s.orw)}% · ATR ${num(s.atrp)}%` },
  { key: "window", label: "Entry window", hint: (s) => (isStructure(s.strategy_type) ? `from ${s.entry_time} IST, once a day` : "after the opening range") },
  { key: "event", label: "Event day", hint: (s) => (s.event_days?.length ? `${s.event_days.length} marked: ${s.event_days.slice(0, 2).join(", ")}${s.event_days.length > 2 ? "…" : ""}` : "none marked = every day") },
  { key: "band", label: "Bollinger band", hint: (s) => `U ${num(s.bb_u)} / L ${num(s.bb_l)}` },
  { key: "stretch", label: "Stretch from VWAP", hint: (s) => `VWAP ${num(s.vwap)}` },
  { key: "move", label: "First-hour move", hint: (s) => `${num(s.fh_move)}%` },
  { key: "confirm", label: "EMA confirmation", hint: (s) => `EMA ${num(s.ema_f)}` },
];

const Mark = ({ ok, id }) => (
  <span data-testid={id} className={`inline-flex h-5 w-5 items-center justify-center rounded ${ok ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/10 text-red-400/80"}`}>
    {ok ? <Check size={12} /> : <X size={12} />}
  </span>
);

const VERDICT = {
  LONG: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  SHORT: "border-red-500/40 bg-red-500/10 text-red-400",
  NO_TRADE: "border-slate-700 bg-slate-800/40 text-slate-300",
  ENTER: "border-indigo-500/40 bg-indigo-500/10 text-indigo-300",
};

export const SignalChecklist = ({ signal }) => {
  if (!signal) return <div className="panel p-4 text-sm text-slate-500" data-testid="panel-signal-checklist">No closed candles yet.</div>;
  const multi = isStructure(signal.strategy_type);
  return (
    <div className="panel p-4" data-testid="panel-signal-checklist">
      <div className="flex items-center justify-between">
        <span className="eyebrow">Signal checklist</span>
        <span className="num text-xs text-slate-500" data-testid="signal-candle-time">candle {istTime(signal.ts)} IST</span>
      </div>
      {signal.strategy_type && signal.strategy_type !== "orb" && <div className="mt-1 text-[11px] text-indigo-300" data-testid="signal-strategy-type">{STRATEGY_LABELS[signal.strategy_type]}</div>}
      {!signal.or_done && !multi && <p className="mt-2 text-xs text-amber-400">Opening range still forming.</p>}
      <table className="dense mt-3 w-full">
        <thead><tr><th>Rule</th>{multi ? <th className="!text-center">Ready</th> : <><th className="!text-center">Long</th><th className="!text-center">Short</th></>}</tr></thead>
        <tbody>
          {RULES.filter((r) => r.key in signal.long).map((r) => (
            <tr key={r.key}>
              <td className="!py-2">
                <div className="text-slate-200">{r.label}</div>
                <div className="num text-[10px] text-slate-500">{r.hint(signal)}</div>
              </td>
              <td className="!text-center"><Mark ok={signal.long[r.key]} id={`check-long-${r.key}`} /></td>
              {!multi && <td className="!text-center"><Mark ok={signal.short[r.key]} id={`check-short-${r.key}`} /></td>}
            </tr>
          ))}
        </tbody>
      </table>
      <div data-testid="card-signal-verdict" className={`mt-4 rounded border px-3 py-3 text-center ${VERDICT[signal.verdict]}`}>
        <div className="eyebrow !text-current opacity-70">Verdict</div>
        <div className="font-display text-2xl font-bold tracking-tight">{signal.verdict.replace("_", " ")}</div>
        <div className="num text-xs opacity-80">close {num(signal.close)}</div>
      </div>
    </div>
  );
};
